/**
 * Workflows topology + streams dashboard.
 *
 * Single screen answering the question: "what's actually wired up
 * right now?" Reads three workflows endpoints and stitches them:
 *
 *   GET /workflows/streams              — known event streams
 *   GET /workflows/streams/depth        — current XLEN per stream
 *   GET /workflows/subscriptions        — active workflow subscriptions
 *
 * Layout — the standard page shell (PageHeader + one rounded-3xl content
 * panel), with the dashboard content inside the panel. It is a dashboard,
 * not a grid: there are no rows, so pagination / search / sort / filter do
 * not apply (D8 of the workflow-grids-standardise plan).
 *   - Controls bar: Refresh, plus when the depth was last observed.
 *   - Stats grid: 4 tiles (producers / streams / subs / total depth).
 *   - For each producer (b2c, myapp, …): a grid of stream cards
 *     showing depth bar (depth / max_length), event-type chips, and
 *     active-subscription count for that stream.
 *   - Bottom: action targets (static — these are services workflows
 *     can call from inside a node, not event sources).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PageHeader } from "@/components/ui/page-header";
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Activity,
  Database,
  Network,
  Zap,
  Box,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  workflowsApi,
  type StreamCatalogEntry,
  type StreamDepthEntry,
  type SubscriptionResponse,
} from "@/services/api/workflows";

export const Route = createFileRoute("/portal/workflows/topology")({
  component: TopologyPage,
});

// Action targets are static — services that workflow nodes call into,
// not event sources. Keeping the list in code (not a DB table) is fine
// because adding a node kind is already a code change.
/**
 * Display labels for the ``source`` field on event streams. CSS
 * ``capitalize`` butchers acronyms ("B2c" instead of "B2C"), so we
 * keep an explicit map and fall back to the raw key when an unknown
 * source appears.
 */
const SOURCE_LABELS: Record<string, string> = {
  b2c: "B2C",
  myapp: "MyApp",
  workflows: "Workflows",
  agentic: "Agentic",
};

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

const ACTION_TARGETS = [
  {
    kind: "action.b2c_write",
    label: "Business2Cloud",
    description:
      "Push records to a B2C target connector (Xero, Cin7, etc.).",
  },
  {
    kind: "action.myapp_record",
    label: "MyApp",
    description: "Create / update / delete rows in a MyApp form.",
  },
  {
    kind: "action.messaging_send",
    label: "Messaging",
    description:
      "Post messages — Teams / email / WhatsApp via existing conversation.",
  },
  {
    kind: "agent.pydantic_ai",
    label: "Agentic",
    description: "Invoke a PydanticAI agent for structured output.",
  },
  {
    kind: "human.approval",
    label: "Human approval",
    description:
      "Pause until a human acts on a MyApp approval row; resume on row update.",
  },
  {
    kind: "logic.branch",
    label: "Logic branch",
    description: "Conditional routing on `${...}` expressions.",
  },
];

function TopologyPage() {
  const { token } = useAuth();
  const [streams, setStreams] = useState<StreamCatalogEntry[]>([]);
  const [depth, setDepth] = useState<StreamDepthEntry[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastPollUnix, setLastPollUnix] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [catalog, depthSnap, subs] = await Promise.all([
        workflowsApi.listStreams(token ?? undefined),
        workflowsApi.getStreamDepth(token ?? undefined),
        workflowsApi.listSubscriptions(token ?? undefined),
      ]);
      setStreams(catalog.streams);
      setDepth(depthSnap.streams);
      setSubscriptions(subs);
      setLastPollUnix(depthSnap.last_poll_unix);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to load topology";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  // Pre-compute per-stream lookups so the grid render stays cheap.
  const depthByStream = useMemo(() => {
    return new Map(depth.map((d) => [d.stream, d]));
  }, [depth]);

  const subsByStream = useMemo(() => {
    const m = new Map<string, number>();
    for (const sub of subscriptions) {
      m.set(sub.stream, (m.get(sub.stream) ?? 0) + 1);
    }
    return m;
  }, [subscriptions]);

  // Group streams by source so each producer gets its own section.
  const streamsBySource = useMemo(() => {
    const groups = new Map<string, StreamCatalogEntry[]>();
    for (const s of streams) {
      const list = groups.get(s.source) ?? [];
      list.push(s);
      groups.set(s.source, list);
    }
    return groups;
  }, [streams]);

  const totalDepth = useMemo(
    () => depth.reduce((sum, d) => sum + d.depth, 0),
    [depth],
  );

  const activeSubs = subscriptions.filter((s) => s.is_active).length;

  return (
    <>
      <Helmet>
        <title>Topology | Workflows | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="Topology"
          description="What's actually wired up right now: producers, streams, active subscriptions, action targets."
          icon={Network}
          eyebrow="Operations"
        />

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {/* One content panel: controls bar, stats grid, then the dashboard
            content. The stream / action-target Cards below are content, not
            a wrapper around it. */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            {lastPollUnix ? (
              <span className="text-xs text-muted-foreground">
                Depth last observed{" "}
                {new Date(lastPollUnix * 1000).toLocaleTimeString()}
              </span>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={loadAll}
              disabled={isLoading}
              aria-label="Refresh"
              className="ml-auto"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* --- Stat tiles ---------------------------------------------- */}
          <div className="grid gap-4 md:grid-cols-4">
            <StatTile
              icon={<Database className="h-4 w-4 text-muted-foreground" />}
              label="Producers"
              value={streamsBySource.size}
            />
            <StatTile
              icon={<Activity className="h-4 w-4 text-muted-foreground" />}
              label="Streams"
              value={streams.length}
            />
            <StatTile
              icon={<Zap className="h-4 w-4 text-muted-foreground" />}
              label="Active subscriptions"
              value={activeSubs}
            />
            <StatTile
              icon={<Box className="h-4 w-4 text-muted-foreground" />}
              label="Events queued (XLEN sum)"
              value={totalDepth.toLocaleString()}
            />
          </div>

          {isLoading && streams.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : null}

          {/* --- Per-producer sections ----------------------------------- */}
          {Array.from(streamsBySource.entries()).map(
            ([source, sourceStreams]) => (
              <section key={source} className="space-y-3">
                <h2 className="text-lg font-semibold">
                  {sourceLabel(source)}{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    ({sourceStreams.length} stream
                    {sourceStreams.length === 1 ? "" : "s"})
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sourceStreams.map((s) => (
                    <StreamCard
                      key={s.stream}
                      stream={s}
                      depth={depthByStream.get(s.stream)}
                      activeSubs={subsByStream.get(s.stream) ?? 0}
                    />
                  ))}
                </div>
              </section>
            ),
          )}

          {/* --- Action targets (static) --------------------------------- */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Action targets</h2>
            <p className="text-sm text-muted-foreground">
              Services workflow nodes can call from inside an execution. Not
              event sources — these run during a workflow, not between
              workflows.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {ACTION_TARGETS.map((t) => (
                <Card key={t.kind} className="p-3">
                  <div className="text-sm font-medium">{t.label}</div>
                  <code className="text-xs text-muted-foreground">{t.kind}</code>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.description}
                  </p>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function StatTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function StreamCard({
  stream,
  depth,
  activeSubs,
}: {
  stream: StreamCatalogEntry;
  depth: StreamDepthEntry | undefined;
  activeSubs: number;
}) {
  // Depth bar — proportional to ratio (depth / max_length). Capped at
  // 100% so an over-cap stream still renders cleanly. Colour shifts at
  // the alert thresholds the depth-monitor doc calls out.
  const ratio = depth?.ratio ?? 0;
  const ratioPct = Math.min(100, Math.round(ratio * 100));
  const barColour =
    ratio >= 0.95
      ? "bg-destructive"
      : ratio >= 0.8
        ? "bg-yellow-500"
        : "bg-primary";

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-mono">{stream.stream}</CardTitle>
        <CardDescription>{stream.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">
              Depth {depth ? depth.depth.toLocaleString() : "—"} /{" "}
              {depth ? depth.max_length.toLocaleString() : "—"}
            </span>
            <span className="text-muted-foreground">{ratioPct}%</span>
          </div>
          <div className="h-1.5 w-full bg-muted rounded">
            <div
              className={`${barColour} h-1.5 rounded`}
              style={{ width: `${ratioPct}%` }}
            />
          </div>
          {depth?.error ? (
            <p className="text-xs text-destructive mt-1">
              Last poll: {depth.error}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-1">
          {stream.event_types.map((t) => (
            <code
              key={t}
              className="text-xs bg-muted px-1.5 py-0.5 rounded"
            >
              {t}
            </code>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Active subscriptions</span>
          <Badge variant={activeSubs > 0 ? "default" : "secondary"}>
            {activeSubs}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
